/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { useApolloClient } from "@apollo/client";
import { AuthApiError } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { notifications } from "../../container/notifications";
import { ModalContext } from "../../modal-provider/modal-context";

export type UseLogoutHook = {
  logout: () => Promise<void>;
  loggingOut: boolean;
};

export const useLogout = (): UseLogoutHook => {
  const { t } = useTranslation();
  const { oktaAuth } = useOktaAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const client = useApolloClient();
  const { showContactSupportModal } = useContext(ModalContext);

  const logout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await oktaAuth.signOut({
        revokeAccessToken: true,
        revokeRefreshToken: true,
        postLogoutRedirectUri: window.location.origin + "/",
      });
      await client.clearStore();
      //To Clear Custom Session Storage eg: Sidebar State
      sessionStorage.clear();
    } catch (error: unknown) {
      if (error instanceof AuthApiError && error?.xhr?.status && error?.xhr?.status !== 401) showContactSupportModal();
      else {
        if (error instanceof Error) {
          notifications.error({
            description: t(error?.message),
          });
        }
      }
    } finally {
      setLoggingOut(false);
    }
  }, [client, oktaAuth, showContactSupportModal, t]);

  return {
    logout,
    loggingOut,
  };
};
